import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  MessageSquare,
  LogOut,
  Home,
  Plus,
  Edit,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSuites = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_per_night: '',
    max_guests: '',
    image_url: '',
    amenities: '',
    available: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchSuites();
  }, []);

  const fetchSuites = async () => {
    try {
      const response = await fetch(`${API}/admin/suites`, { credentials: 'include' });
      const data = await response.json();
      setSuites(data);
    } catch (error) {
      console.error('Error fetching suites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const openCreateModal = () => {
    setEditingSuite(null);
    setFormData({
      name: '',
      description: '',
      price_per_night: '',
      max_guests: '',
      image_url: '',
      amenities: '',
      available: true
    });
    setSelectedFile(null);
    setShowModal(true);
  };

  const openEditModal = (suite) => {
    setEditingSuite(suite);
    setFormData({
      name: suite.name,
      description: suite.description,
      price_per_night: suite.price_per_night.toString(),
      max_guests: suite.max_guests.toString(),
      image_url: suite.image_url,
      amenities: suite.amenities.join(', '),
      available: suite.available
    });
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return formData.image_url;

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', selectedFile);

    try {
      const response = await fetch(`${API}/admin/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Image upload failed. Please try again.');
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload image if file selected
      let imageUrl = formData.image_url;
      if (selectedFile) {
        imageUrl = await uploadImage();
      }

      const amenitiesArray = formData.amenities
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      const suiteData = {
        name: formData.name,
        description: formData.description,
        price_per_night: parseInt(formData.price_per_night),
        max_guests: parseInt(formData.max_guests),
        image_url: imageUrl,
        amenities: amenitiesArray,
        available: formData.available
      };

      if (editingSuite) {
        await fetch(`${API}/admin/suites/${editingSuite.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(suiteData)
        });
      } else {
        await fetch(`${API}/admin/suites`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(suiteData)
        });
      }
      
      setShowModal(false);
      fetchSuites();
    } catch (error) {
      console.error('Error saving suite:', error);
    }
  };

  const handleDelete = async (suiteId) => {
    if (!window.confirm('Are you sure you want to delete this suite?')) return;

    try {
      await fetch(`${API}/admin/suites/${suiteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchSuites();
    } catch (error) {
      console.error('Error deleting suite:', error);
    }
  };

  const toggleAvailability = async (suite) => {
    try {
      await fetch(`${API}/admin/suites/${suite.id}/availability`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ available: !suite.available })
      });
      fetchSuites();
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  return (
    <div data-testid="admin-suites-page" className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <div className="bg-[#1A1A1A] text-[#FAF8F5] py-4 px-6 md:px-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-[#C9A84C]">
              De Azraa
            </h1>
            <span className="text-xl font-medium">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm hover:text-[#C9A84C] transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 md:px-12 py-4">
        <nav className="flex gap-8">
          <Link
            to="/admin/dashboard"
            data-testid="nav-dashboard"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </Link>
          <Link
            to="/admin/bookings"
            data-testid="nav-bookings"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <Calendar size={16} />
            Bookings
          </Link>
          <Link
            to="/admin/suites"
            data-testid="nav-suites"
            className="flex items-center gap-2 text-sm font-medium text-[#C9A84C] border-b-2 border-[#C9A84C] pb-2"
          >
            <Home size={16} />
            Suites
          </Link>
          <Link
            to="/admin/contacts"
            data-testid="nav-contacts"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <MessageSquare size={16} />
            Contact Messages
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12">
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A]">
              Manage Suites
            </h2>
            <button
              onClick={openCreateModal}
              data-testid="create-suite-btn"
              className="flex items-center gap-2 bg-[#C9A84C] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-6 py-3 text-sm tracking-widest uppercase"
            >
              <Plus size={16} />
              Create Suite
            </button>
          </div>

          {loading ? (
            <p className="text-center py-8 text-[#3A3A3A]">Loading suites...</p>
          ) : suites.length === 0 ? (
            <p className="text-center py-8 text-[#3A3A3A]" data-testid="no-suites">No suites found</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {suites.map((suite, index) => (
                <div
                  key={suite.id}
                  data-testid={`suite-card-${index}`}
                  className="border border-[#E5E5E5] overflow-hidden"
                >
                  <div className="relative h-48">
                    <img
                      src={suite.image_url}
                      alt={suite.name}
                      className="w-full h-full object-cover"
                    />
                    <div className={`absolute top-2 right-2 px-3 py-1 text-xs font-medium ${
                      suite.available ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {suite.available ? 'Available' : 'Unavailable'}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-xl text-[#1A1A1A] mb-2">{suite.name}</h3>
                    <p className="text-sm text-[#3A3A3A] mb-3 line-clamp-2">{suite.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-medium text-[#1A1A1A]">
                        ₦{suite.price_per_night.toLocaleString()}/night
                      </span>
                      <span className="text-sm text-[#3A3A3A]">
                        Max: {suite.max_guests} guests
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(suite)}
                        data-testid={`edit-suite-${index}`}
                        className="flex-1 flex items-center justify-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-4 py-2 text-sm"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => toggleAvailability(suite)}
                        data-testid={`toggle-availability-${index}`}
                        className={`flex-1 px-4 py-2 text-sm transition-colors ${
                          suite.available
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {suite.available ? 'Mark Unavailable' : 'Mark Available'}
                      </button>
                      <button
                        onClick={() => handleDelete(suite.id)}
                        data-testid={`delete-suite-${index}`}
                        className="flex items-center justify-center border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors px-4 py-2"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div
            data-testid="suite-modal"
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6 overflow-y-auto py-8"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A]">
                  {editingSuite ? 'Edit Suite' : 'Create New Suite'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-[#3A3A3A] hover:text-[#1A1A1A]"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} data-testid="suite-form" className="space-y-4">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Suite Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C]"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Price per Night (₦) *
                    </label>
                    <input
                      type="number"
                      name="price_per_night"
                      value={formData.price_per_night}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Max Guests *
                    </label>
                    <input
                      type="number"
                      name="max_guests"
                      value={formData.max_guests}
                      onChange={handleChange}
                      required
                      min="1"
                      max="10"
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Suite Image *
                  </label>
                  
                  {/* File Upload */}
                  <div className="mb-3">
                    <label className="block w-full border-2 border-dashed border-[#E5E5E5] hover:border-[#C9A84C] transition-colors p-6 text-center cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-[#3A3A3A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-[#3A3A3A]">
                          {selectedFile ? selectedFile.name : 'Click to upload image'}
                        </span>
                        <span className="text-xs text-[#3A3A3A]/70">
                          JPG, PNG, or WebP (max 5MB)
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* Or URL Input */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#E5E5E5]"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-[#3A3A3A]">Or use URL</span>
                    </div>
                  </div>

                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] mt-3"
                    disabled={!!selectedFile}
                  />
                  {selectedFile && (
                    <p className="text-xs text-[#3A3A3A] mt-1">Image file selected. URL input disabled.</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Amenities (comma-separated) *
                  </label>
                  <textarea
                    name="amenities"
                    value={formData.amenities}
                    onChange={handleChange}
                    required
                    rows="3"
                    placeholder="Fully Equipped Kitchen, 24-Hour Power Supply, High-Speed WiFi, ..."
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] resize-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="available"
                    id="available"
                    checked={formData.available}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <label htmlFor="available" className="text-sm text-[#1A1A1A]">
                    Available for booking
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-8 py-3 text-sm tracking-widest uppercase"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="save-suite-btn"
                    disabled={uploadingImage}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#C9A84C] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-8 py-3 text-sm tracking-widest uppercase disabled:opacity-50"
                  >
                    <Save size={16} />
                    {uploadingImage ? 'Uploading...' : editingSuite ? 'Update Suite' : 'Create Suite'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminSuites;
